import { CurrencyPipe, DatePipe } from '@angular/common';
import { HttpClient, HttpParameterCodec, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, Subject, Subscription, catchError, finalize, of, take, throwError } from 'rxjs';
import { constants } from 'src/app/config/constant';
import { environment } from 'src/environments/environment';
import { z } from 'zod';
import { AccessModuleResponse, Country } from '../model/api';
import { CPUModuleService } from './components/CPUModule.service';
import { CREDITVOUCHER, ReceiptPrinterModule } from './components/ReceiptPrinterModule.service';
import { db } from './db.service';
import { LockdownService } from './lockdown.service';
import { LogService } from './log.service';
import mockapi from './mock.api.service';
import { SignalrService } from './signalr.service';
import { StorageService } from './storage.service';
import { API_SUNCASH_DEPOSIT, API_SUNCASH_WITHDRAW } from './suncash.service';
import { TraceService } from './trace.service';
import { WU_END_POINT } from './western-union.service';
import { GAMING_ENDPOINTS } from './gaming.service';

export type GenericJSON<T = any> = { [name: string]: T }
type URLRequest = { method: string, params: { [name: string]: string }, response_data?: string, types?: ThisType<"types">, response_types?: ThisType<"response_types"> }
type APIParams<TUrl extends URLRequest> = { [Property in keyof TUrl['types']as keyof TUrl['params'][Property]]: TUrl['types'][Property] };
// type KnownKeys<T> = {
//   [K in keyof T]: string extends K ? never : number extends K ? never : K
// } extends { [_ in keyof T]: infer U } ? U : never;
// type KnownPart<T> = Pick<T, KnownKeys<T>>
// https://stackoverflow.com/questions/65013802/loose-type-definition-with-omit-and-keystring-unknown

type EventLog = {
  type: 'event',
  name: string,
  event: any,
  timestamp: number
}

@Injectable({
  providedIn: 'root'
})
export class ApiHttpService {
  static SUCCESS_CODE = "0000"
  static ERROR_CODE = "1000"
  muted: boolean = false;

  showLoader: boolean = true;
  requests: { method: string, source: Subscription, idx: string }[] = [];

  constructor(
    private http: HttpClient,
    private config: constants,
    public storage: StorageService,
    public cpu_service: CPUModuleService,
    public receipt_module: ReceiptPrinterModule,
    public traceService: TraceService,
    public logger: LogService,
    public router: Router,
    public datepipe: DatePipe,
    public currencypipe: CurrencyPipe,
    public signalR: SignalrService,
    public lockdownService: LockdownService,
  ) {
  }

  //Loader Show/Hide
  private loaderSubject = new Subject<Boolean>();
  loaderState = this.loaderSubject.asObservable();
  loader(value: any) {
    if (!this.muted) {
      if (!value && this.requests.length > 0) {
        return;
      }
      this.loaderSubject.next(value);
    }
  }

  postData(value: HttpParams, options: any = {}) {
    return this.http.post<any>(this.config.API_ENDPOINT, value)
  }

  getData(params: URLSearchParams) {
    return this.http.get<any>(this.config.API_ENDPOINT + "?" + params);
  }

  mute(val: boolean = true) {
    this.muted = val
    return this;
  }

  cancelOld(api: URLRequest) {
    this.requests = this.requests.filter((request) => {
      if (request.method == api.method) {
        request.source.unsubscribe()
        return false;
      }
      return true;
    })
    return this;
  }

  // https://stackoverflow.com/a/10574546/6458222
  extractJSON(str: string) {
    let firstOpen = 0;
    let firstClose;
    let candidate;
    firstOpen = str.indexOf('{', firstOpen + 1);
    do {
      firstClose = str.lastIndexOf('}');
      if (firstClose <= firstOpen) {
        return str;
      }
      do {
        candidate = str.substring(firstOpen, firstClose + 1);
        try {
          var res = JSON.parse(candidate);
          return res;
        }
        catch (e) { }
        firstClose = str.substr(0, firstClose).lastIndexOf('}');
      } while (firstClose > firstOpen);
      firstOpen = str.indexOf('{', firstOpen + 1);
    } while (firstOpen != -1);
    return str;
  }
  // { [Property in keyof KnownPart<TUrl['types']>]: TUrl['types'][Property] }
  // how to prevent dynamic object assignment to body?
  post
    <TUrl extends URLRequest, TBody extends { [Parameter in keyof TUrl['params']]: unknown }>
    (url: TUrl, body: TBody & TUrl['types'], useCustomEncoder: boolean = false): Promise<{ message: string, data: Required<TUrl>['response_types'], code: string }> {
    let userPresent = setInterval(() => {
      this.lockdownService.setUserPresent()
    }, 1000);
    this.signalR.log('AppLog_Info', { message: 'SetUserPresent interval called' })
    let { method, data } = this.processParams<TUrl>(url, body)

    this.signalR.log('AppLog_Info', {
      message: "Transaction Request",
      metadata: {
        method,
        data
      }
    })

    // if(method=='feeInquiry'){
    //   return
    // }
    return new Promise(async (resolve, reject) => {
      let voucherData = new HttpParams(useCustomEncoder ? { encoder: new CustomURLEncoder() } : {})
        .set('method', method)
        .set('return_mode', this.config.RETURN_MODE);

      for (let key in data) {
        voucherData = voucherData.set(key, (<any>data)[key])
      }
      this.loader(true)
      let source$ = this.postData(voucherData)
      // lastValueFrom(source$)
      let idx = crypto.randomUUID()
      let source = source$.pipe(take(1), finalize(() => {
        this.requests = this.requests.filter(request => request.idx != idx)
        this.loader(false)
        clearInterval(userPresent)
        this.signalR.log('AppLog_Info', { message: 'SetUserPresent interval cleared' })
      }), catchError(e => {
        try {
          // malformed = true
          this.signalR.log('AppLog_Info', {
            message: "Transaction Response Malformed",
            metadata: {
              method,
              response: e.error.text
            }
          })
          let a = this.extractJSON(e.error.text)
          return of(a);
        } catch (_) { }
        return of(e);
      })).subscribe((request) => {
        let mock = !environment.production
        if (mock && typeof (<any>mockapi)[method] != "undefined") {
          request = (<any>mockapi)[method]
        }
        if (typeof request.ResponseCode != "undefined") {

          if (request.ResponseCode == ApiHttpService.SUCCESS_CODE) {
            let useResponseMessage = [this.config.API_COMPUTE_FEE.method, this.config.API_GAME_DEPOSIT.method, this.config.API_GAME_WITHDRAW.method, this.config.API_GAME_VALIDATE.method, this.config.API_SUNCASH_LOGIN.method, API_SUNCASH_DEPOSIT.method, API_SUNCASH_WITHDRAW.method, this.config.API_INT_GIFT_CARD_SEND.method, this.config.API_MAINTENANCE_LOGIN.method, this.config.API_SAND_DOLLAR_LOAD.method, this.config.API_MOBILE_WALLET_LOAD.method, this.config.API_BUSINESS_PAYMENT.method, this.config.GOVERNMENT_PAY_REFERENCE, this.config.GOVERNMENT_PAY_INVOICE, this.config.API_SEND_MONEY.method, this.config.API_TOPUP_TRANSACTION.method, this.config.API_TOPUP_BILLINFO.method, this.config.API_UTILITY.method, this.config.API_UTILITY_BILL_INFO.method, this.config.API_SESSION.method, this.config.API_RECENT_UTILITY.method, this.config.API_BTC_PRODUCT_PLANS.method, this.config.API_CARD_WITHDRAWAL.method, this.config.API_CARD_DEPOSIT.method, this.config.API_ZTRAIL.method, this.config.API_GENERATE_SETTLEMENT.method, this.config.API_GET_TX_COUNT.method, this.config.API_SUBMIT_SETTLEMENT.method, this.config.API_PREV_SETTLEMENT.method, this.config.API_POPULAR_PROVIDERS.method, WU_END_POINT.CALCULATE_PRICE.method, WU_END_POINT.CUSTOMER_INFO.method, WU_END_POINT.GET_RECEIVER.method, , WU_END_POINT.GET_RELATION.method, WU_END_POINT.GET_SOURCE.method, , WU_END_POINT.GET_PURPOSE.method, WU_END_POINT.GENERATE_KYC_QR.method, WU_END_POINT.GENERATE_RECEIVER_QR.method, WU_END_POINT.SEND_KYC_SMS.method, WU_END_POINT.SEND_RECEIVER_SMS.method, WU_END_POINT.SEND_MONEY.method, WU_END_POINT.COMPUTE_SEND_AMOUNT.method, GAMING_ENDPOINTS.GAMING_HOUSES.method];

            if (useResponseMessage.indexOf(method) >= 0) {
              request.ResponseMessage = {
                ResponseData: request.ResponseMessage?.ResponseData || request.ResponseMessage,
                ResponseMessage: request.ResponseMessage?.ResponseMessage || ''
              }
            }

            this.mute(false)
            let parsed = {
              code: request.ResponseCode,
              message: request.ResponseMessage.ResponseMessage,
              data: request.ResponseMessage[typeof url !== "string" && typeof url.response_data != "undefined" ? url.response_data : "ResponseData"],
            }

            this.signalR.log('AppLog_Info', {
              message: "Transaction Completed",
              metadata: {
                api: method,
                ...parsed
              }
            })
            resolve(parsed)
          } else {
            this.mute(false)
            let parsed = {
              code: request.ResponseCode,
              message: request.ResponseMessage.toString().toLowerCase().includes("invalid method name") ? "Service error" : request.ResponseMessage
            }
            this.signalR.log('AppLog_Error', {
              message: "Transaction Failed",
              metadata: {
                api: method,
                ...parsed
              }
            })
            reject(parsed)
          }
        } else {
          this.mute(false)
          this.signalR.log('AppLog_Error', {
            message: "Transaction Failed",
            metadata: {
              api: method,
              response: request
            }
          })
          reject(request)
        }
      })
      this.requests.push({
        method,
        source,
        idx
      });
    })
  }

  processParams<TUrl extends URLRequest>(url: TUrl, data: TUrl['types']): {
    method: string,
    data: APIParams<TUrl>,
  } {
    let method: string = url.method
    let tmp: any = {}
    for (let _key in data) {
      if (typeof url.params[_key] != "undefined") {
        tmp[url.params[_key]] = (<any>data)[_key]
      }
    }
    data = tmp
    return { method, data } as { method: string, data: APIParams<TUrl> }
  }

  toParams(data: GenericJSON) {
    let params = "";
    for (let key in data) {
      params += (params ? '&' : '') + [key, data[key]].join('=');
    }
    return params;
  }

  ztrail(request: any, data: any) {
    let user: AccessModuleResponse = this.storage.get<any>('user')

    let trail_data = {
      ...data,
      session: user.SessionID,
      merchant_id: user.MerchantId,
      branch_code: user.BranchCode,
      terminal_code: user.TerminalCode,
      terminal_reference: user.TerminalId,
      location: user.BranchName,
      method_name: request.method,
      request_params: btoa(this.toParams(request.data)),
    }
    db.logs.add({
      trail: JSON.stringify(trail_data),
      date_created: new Date,
    })

    return this.mute().post(this.config.API_ZTRAIL, trail_data)
  }

  notifyAdmin(subject: string, message: string) {
    let user: AccessModuleResponse = this.storage.get<any>('user')
    return this.mute().post(this.config.API_SEND_MAIL, {
      session: user.SessionID,
      email: this.config.ADMIN_EMAIL,
      subject, message
    })
  }

  kioskLog(log: EventLog) {
    let session = this.storage.get('user.SessionID')
    let terminal = this.storage.get('user.TerminalId');
    return this.mute().post(this.config.API_LOG, { session, terminal, content: JSON.stringify(log) })
  }

  printCreditVoucher(data: { cash: number, service: string, message?: string, fees?: { fee?: number, vat?: number } }, error?: any, print: boolean = true) {
    var session = localStorage.getItem('SessionID') as string;
    var user: AccessModuleResponse = this.storage.get<any>('user')
    let voucher = this.mute().post(this.config.API_CREDIT_VOUCHER, {
      session,
      kioskId: (user.TerminalCode.split('-')[1]) || '',
      mobile: '',
      email: '',
      amount: data.cash,
      fee: data?.fees?.fee || 0,
      vat: data?.fees?.vat || 0,
    })
    if (!print) {
      return voucher as any;
    }

    return voucher.then(async (response) => {
      let terminal_name = await this.cpu_service.getName()
      let CreditVoucherData: CREDITVOUCHER = {
        settlement_time: (this.datepipe.transform(new Date, 'MM-dd-yyyy hh:mm a') || '').toUpperCase(),
        terminal_id: terminal_name,
        voucher_value: this.currencypipe.transform(response.data.amount) || '',
        voucher_code: response.data.voucher_code,
        voucher_pin: response.data.pin,
        service: data.service,
        message: data.message,
        fee: data?.fees?.fee || 0,
        vat: data?.fees?.vat || 0,
        ref_number: session,
      }
      return this.receipt_module.printCreditVoucherReceipt(CreditVoucherData)
    }).then((res: any) => {
      if (error) {
        this.signalR.log('AppLog_Error', {
          message: "requesting credit voucher",
          metadata: {
            error,
          }
        })
        this.storage.set<any>("result", { "ResponseCode": error.code, error_message: error.message })
      }
    }).catch((_error) => {
      this.storage.set<any>("result", {
        CREDITVOUCHER_FAILED: { amount: data.cash, ref: session, kioskId: user.TerminalCode, },
        "ResponseCode": _error.code,
        error_message: `
          Failed to print the credit voucher. The credit voucher info is below. Please take a picture or record the info below for assistence.
        `
      })
    }).finally(() => {
      this.router.navigate(['kiosk/result']).then(() => {
        this.traceService.pageNavigation('Result', "DepositCash@deposit", 'info', 'Result', 'kiosk/result', false)
      })
    })
  }

  getCountries(): Promise<z.infer<typeof Country>[]> {
    let countries = this.storage.get('countries')
    if (countries && countries.length > 0) {
      return Promise.resolve(countries)
    }
    let session = this.storage.get('user.SessionID')
    return this.post(this.config.API_COUNTRY_LIST, {
      session,
    }).then((response) => {
      this.storage.set('countries', response.data);
      return response.data;
    }).catch((request) => {
      this.logger.log('Error : ' + request.message);
      return [] as any;
    })
  }
}

export class CustomURLEncoder implements HttpParameterCodec {
  encodeKey(key: string): string {
    return encodeURIComponent(key);
  }
  encodeValue(key: string): string {
    return encodeURIComponent(key);
  }
  decodeKey(key: string): string {
    return decodeURIComponent(key);
  }
  decodeValue(key: string) {
    return decodeURIComponent(key);
  }
}
